import React, { useMemo, useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { CellInfo } from 'react-table';
import { observer } from 'mobx-react';
import PageContainer from 'src/components/PageContainer';
import PageContent from 'src/components/PageContent';
import NavBar from '../NavBar';
import TabNav from 'src/components/TabNav';
import Table from 'src/components/Table';
import Button from 'src/components/Button';
import DateComponent from 'src/components/DateComponent';
import Flex from 'src/components/Flex';
import Input from 'src/components/Input';
import Pagination from 'src/components/Table/Pagination';
import { useDocumentScans, DocumentScanListItem } from 'src/queries/useDocumentScans';
import styles from './styles.module.scss';
import FacilityDropDown from 'src/components/FacilityDropDown';
import FilterStore from 'src/stores/FilterStore';
import UserStore from 'src/stores/UserStore';
import { FilterQueryParams } from 'src/utils/types';
import { ICONS, ROUTES } from 'src/utils/constants';
import Icon from 'src/components/Icon';
import * as variables from 'src/styles/variables';
import StatusBadge, { StatusBadgeColors } from 'src/components/StatusBadge';

const TABS = ['Successful', 'Unsuccessful'];
const FILTER_PAGE = 'documentScansPage';

function getStatusBadgeColor(status: string) {
    if (status === 'complete' || status === 'extracted' || status === 'uploaded') {
        return StatusBadgeColors.Green;
    }
    if (status === 'failed') {
        return StatusBadgeColors.Red;
    }
    return StatusBadgeColors.Yellow;
}

function getDocumentTypeIconName(documentScan: DocumentScanListItem): string | null {
    if (documentScan.documentTypeTarget === 'legacy') {
        return null;
    }
    if (documentScan.documentTypeTarget === 'encounter_form') {
        return ICONS.NotesBookText;
    }
    return ICONS.PatientVisit;
}

function getOutcomeIconName(outcome: 'created' | 'updated') {
    return outcome === 'created' ? ICONS.PlusButton : ICONS.SynchronizeArrow1;
}

function getCreatedDateValue(value: unknown) {
    if (!value) {
        return null;
    }
    return value instanceof Date ? value.toISOString() : (value as string | number);
}

function DocumentScans() {
    const history = useHistory();
    const [selectedFacility, setSelectedFacility] = useState<number | null>(null);
    const [selectedTab, setSelectedTab] = useState(TABS[0]);
    const [encounterNumberInput, setEncounterNumberInput] = useState('');
    const [encounterNumberFilter, setEncounterNumberFilter] = useState('');
    const filterStore = useContext(FilterStore);
    const userStore = useContext(UserStore);
    const documentScansFilters: FilterQueryParams = filterStore[FILTER_PAGE] || { pagination: { page: 1, limit: 25 } };

    const hasEncounter = selectedTab === TABS[0] ? true : false;

    useEffect(() => {
        const timer = setTimeout(() => {
            setEncounterNumberFilter(encounterNumberInput);
        }, 500);
        return () => clearTimeout(timer);
    }, [encounterNumberInput]);

    // Reset pagination when filter changes
    useEffect(() => {
        if (encounterNumberFilter !== '') {
            filterStore.setFilters(FILTER_PAGE, {
                ...documentScansFilters,
                pagination: { ...(documentScansFilters.pagination || { page: 1, limit: 25 }), page: 1 },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [encounterNumberFilter]);

    const handleEncounterNumberChange = (value: string) => {
        setEncounterNumberInput(value);
    };

    // Reset pagination and encounter filter when tab changes
    useEffect(() => {
        setEncounterNumberInput('');
        setEncounterNumberFilter('');
        filterStore.setFilters(FILTER_PAGE, {
            ...documentScansFilters,
            pagination: { ...(documentScansFilters.pagination || { page: 1, limit: 25 }), page: 1 },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTab]);

    const { data: queryResult, isLoading, error } = useDocumentScans(
        selectedFacility || 0,
        userStore.selectedOrg?.orgId,
        {
            hasEncounter,
            encounterNumber: encounterNumberFilter,
            pagination: documentScansFilters.pagination || { page: 1, limit: 25 },
        },
    );

    const documentScans = queryResult?.data || [];
    const totalCount = queryResult?.count || 0;

    const handleFacilityChange = (facility: any) => {
        setSelectedFacility(facility?.value || null);
        // Reset pagination when facility changes
        filterStore.setFilters(FILTER_PAGE, {
            ...documentScansFilters,
            pagination: { ...(documentScansFilters.pagination || { page: 1, limit: 25 }), page: 1 },
        });
    };

    const handlePaginationFilterChange = (page: number) => {
        filterStore.setFilters(FILTER_PAGE, {
            ...documentScansFilters,
            pagination: { ...(documentScansFilters.pagination || { page: 1, limit: 25 }), page },
        });
    };

    const columns = useMemo(() => {
        const baseColumns = [
            {
                Header: 'DOCUMENT TYPE',
                id: 'documentType',
                minWidth: 200,
                accessor: (row: DocumentScanListItem) => row.displayName,
                Cell: (props: CellInfo) => {
                    const iconName = getDocumentTypeIconName(props.original);
                    return (
                        <div className={styles.iconTextCell}>
                            {iconName && (
                                <Icon
                                    name={iconName}
                                    size={18}
                                    color={variables.darkBlue}
                                    className={styles.cellIcon}
                                />
                            )}
                            <div className={styles.documentTypeName}>{props.value}</div>
                        </div>
                    );
                },
            },
            {
                Header: 'STATUS',
                id: 'scanStatus',
                minWidth: 130,
                accessor: (row: DocumentScanListItem) => row.status,
                Cell: (props: CellInfo) => (
                    <StatusBadge status={getStatusBadgeColor(props.value)} text={String(props.value).toUpperCase()} />
                ),
            },
            {
                Header: 'Images',
                id: 'images',
                minWidth: 120,
                accessor: (row: DocumentScanListItem) => row.imageCount,
            },
            {
                Header: 'OUTCOME',
                id: 'outcome',
                minWidth: 140,
                accessor: (row: DocumentScanListItem) => row.outcome,
                Cell: (props: CellInfo) => {
                    if (!props.value) {
                        return '—';
                    }
                    const outcomeText = props.original.outcomeDisplayName || (props.value === 'created' ? 'Created' : 'Updated');
                    return (
                        <div className={styles.iconTextCell}>
                            <Icon
                                name={getOutcomeIconName(props.value)}
                                size={16}
                                color={variables.darkBlue}
                                className={styles.cellIcon}
                            />
                            <span>{outcomeText}</span>
                        </div>
                    );
                },
            },
            {
                Header: 'ARCHIVED',
                accessor: 'archived',
                minWidth: 120,
                Cell: (props: CellInfo) => (props.value ? <StatusBadge status={StatusBadgeColors.Yellow} text="ARCHIVED" /> : 'No'),
            },
            {
                Header: 'CREATED',
                accessor: 'createdAt',
                minWidth: 150,
                Cell: (props: CellInfo) => <DateComponent date={getCreatedDateValue(props.value)} showDateTime={true} />,
            },
            {
                Header: 'ACTIONS',
                minWidth: 150,
                style: { paddingVertical: 0, paddingHorizontal: 20 },
                Cell: (props: CellInfo) => (
                    <Flex direction="row" align="center">
                        <Button
                            type="small"
                            text="View"
                            onClick={() => handleView(props.original)}
                            className={styles.actionButton}
                            data-test-id="view-document-scan-button"
                        />
                    </Flex>
                ),
            },
        ];

        if (selectedTab === TABS[0]) {
            return [
                {
                    Header: 'ENCOUNTER NUMBER',
                    id: 'encounterNumber',
                    accessor: (row: DocumentScanListItem) => row.encounterNumber || null,
                    minWidth: 150,
                    Cell: (props: CellInfo) => props.value || '—',
                },
                ...baseColumns,
            ];
        }

        return baseColumns;
    }, [selectedTab]);

    function handleView(scan: DocumentScanListItem) {
        history.push(ROUTES.getString(ROUTES.DocumentScan, undefined, { documentScanId: scan.documentScanId }));
    }

    return (
        <PageContainer>
            <NavBar />
            <PageContent>
                <Flex direction="column" className={styles.container}>
                    <Flex direction="column" className={styles.header}>
                        <h1 className={styles.pageTitle}>Document Scans</h1>
                        <p className={styles.pageSubtitle}>Manage document scans for different facilities</p>
                    </Flex>
                    <Flex direction="row" align="center" justify="between" className={styles.controlsRow}>
                        <Flex className={styles.filterWrap}>
                            <FacilityDropDown
                                onChange={handleFacilityChange}
                                selectedValue={selectedFacility}
                                allowEmpty={false}
                                placeholder="Filter by Facility"
                            />
                        </Flex>
                    </Flex>
                </Flex>
                <div className={styles.tabContainer}>
                    <TabNav
                        tabs={TABS}
                        onTabClick={(tab: string) => {
                            setSelectedTab(tab);
                        }}
                    />
                </div>
                {selectedTab === TABS[0] && (
                    <div className={styles.searchRow}>
                        <div className={styles.searchInputWrapper}>
                            <Icon name={ICONS.Search} size={16} color={variables.grey} className={styles.searchIcon} />
                            <Input
                                placeholder="Search by Encounter #"
                                value={encounterNumberInput}
                                onChangeText={handleEncounterNumberChange}
                                wrapClassName={styles.encounterFilterInput}
                            />
                        </div>
                    </div>
                )}
                <Table
                    columns={columns}
                    data={documentScans}
                    isLoading={isLoading}
                    showPagination={false}
                    pageSize={documentScansFilters.pagination?.limit || 25}
                />
                {documentScans.length > 0 && (
                    <Pagination
                        pages={Math.ceil(totalCount / (documentScansFilters.pagination?.limit || 25)) || 1}
                        page={(documentScansFilters.pagination?.page || 1) - 1}
                        onPageChange={(page: number) => handlePaginationFilterChange(page + 1)}
                        showing={documentScans.length}
                        totalRecords={totalCount}
                    />
                )}
            </PageContent>
        </PageContainer>
    );
}

export default observer(DocumentScans);
